import TASPositivePayCheckExceptionsSearch from "../TASPositivePayCheckExceptionsSearch";

/**
 * @class TASPositivePayCheckExceptionsSearchUI
 * @classdesc Class definition for controlling the properties that are set in a search feature.
 * 
 * @property {TASPositivePayCheckExceptionsSearch} _search - The instance of search on the page.
 *  * @property {OBJECT} _didSearch - The ui for search capabilities.
 *  * @property {OBJECT} _didSaveChanges - The ui for the save capabilities.
 *  * @property {OBJECT} _mobile - The ui for the. mobile view.
 *  * @property {OBJECT} _account - The ui for the account.
 *  * @property {OBJECT} _saveAndCancelButtons - The ui for the buttons on the page.
 */
export default class TASPositivePayCheckExceptionsSearchUI {
    constructor(record) {
        this.search = record.search;
        this.didSearch = record.didSearch;
        this.didSaveChanges = record.didSaveChanges;
        this.mobile = record.mobile;
        this.account = record.account;
        this.saveAndCancelButtons = record.saveAndCancelButtons;
    }

    /**
     * @name search
     * @instance
     * @property set {STRING} - sets the search value of the class.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set search(value) { this._search = value; }

    /**
     * @name didSearch
     * @instance
     * @property set {BOOLEAN} - sets whether a search has been performed.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set didSearch(val) {
        this._didSearch = val;
    }

    /**
     * @name account
     * @instance
     * @property set {STRING} - sets the selected account.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set account(val) {
        this._account = val;
    }

    /**
     * @name mobile
     * @instance
     * @property set {BOOLEAN} - sets whether the mobile view is open or closed.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set mobile(val) {
        this._mobile = val;
    }

    /**
     * @name didSaveChanges
     * @instance
     * @property set {BOOLEAN} - sets whether changes have been saved.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set didSaveChanges(val) {
        this._didSaveChanges = val;
    }

    /**
     * @name saveAndCancelButtons
     * @instance
     * @property set {BOOLEAN} - sets the save and cancel button controls.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set saveAndCancelButtons(val) {
        this._saveAndCancelButtons = val;
    }

    /**
     * @name search
     * @instance
     * @property get {STRING} - returns the search instance that holds data about the search criteria.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get search() { return this._search; }

    /**
     * @name didSearch
     * @instance
     * @property get {STRING} - returns the search status of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get didSearch() { return this._didSearch; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get account() {
        return this._account;
    }

    /**
     * @name mobile
     * @instance
     * @property get {BOOLEAN} - returns whether the mobile view is open or closed.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get mobile() {
        return this._mobile;
    }

    /**
     * @name didSaveChanges
     * @instance
     * @property get {BOOLEAN} - returns whether changes have been saved.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get didSaveChanges() {
        return this._didSaveChanges;
    }

    /**
     * @name saveAndCancelButtons
     * @instance
     * @property get {BOOLEAN} - returns the save and cancel button controls.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get saveAndCancelButtons() {
        return this._saveAndCancelButtons;
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    get record() {
        return {
            search: this.search,
            didSearch: this.didSearch,
            didSaveChanges: this.didSaveChanges,
            mobile: this.mobile,
            account: this.account,
            saveAndCancelButtons: this.saveAndCancelButtons
        };
    }

    static defaultVals() {
        return { 
            search: TASPositivePayCheckExceptionsSearch.init(),
            didSearch: { isActive: true },
            didSaveChanges: { isActive: false, numberOfChangesSaved: 0 },
            mobile: { isActive: true },
            account: { isActive: false },
            saveAndCancelButtons: { isActive: false }
        };
    }

    reset() {
        const defaultVals = TASPositivePayCheckExceptionsSearchUI.defaultVals();
        return TASPositivePayCheckExceptionsSearchUI.init(defaultVals);
    }
    
    static init(record) {
        const defaultVals = TASPositivePayCheckExceptionsSearchUI.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayCheckExceptionsSearchUI(vals);
    }
}